import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PracticeMode } from '../types'
import { formatClock } from '../utils'

export interface GameHudProps {
  readonly mode: PracticeMode
  /** Durée totale en ms — uniquement pertinent en mode `chrono`. */
  readonly durationMs?: number
  /** Fait tourner le chrono quand vrai ; le fige (sans le remettre à zéro) quand faux. */
  readonly running: boolean
  readonly score: number
  readonly currentRound: number
  readonly totalRounds: number
  /** Appelé une fois, quand le temps imparti est écoulé (mode `chrono` uniquement). */
  readonly onTimeUp?: () => void
}

/**
 * Bandeau de jeu : chronomètre, score, progression.
 *
 * Le chrono s'appuie sur `performance.now()` et `requestAnimationFrame` —
 * jamais `Date.now()` ni `setTimeout`, dont la dérive fausserait la latence
 * mesurée et, par ricochet, la note SM-2 dérivée de cette latence (SPEC §10).
 */
export function GameHud({ mode, durationMs, running, score, currentRound, totalRounds, onTimeUp }: GameHudProps) {
  const { t } = useTranslation()
  const [elapsedMs, setElapsedMs] = useState(0)
  const elapsedRef = useRef(0)
  const frameRef = useRef<number | null>(null)
  const timeUpFiredRef = useRef(false)

  // `onTimeUp` est recréé à chaque rendu de `GameShell` (nouvelle closure sur
  // `answers` à chaque réponse). Le garder hors du tableau de dépendances de
  // l'effet évite de redémarrer l'horloge à chaque manche : un redémarrage
  // recalcule `startedAt` depuis `elapsedRef`, qui n'est mis à jour qu'à
  // chaque frame — le reliquat écoulé depuis la dernière frame (jusqu'à
  // ~16 ms) serait perdu à chaque réponse, et dériverait de plusieurs
  // centaines de ms sur une partie longue.
  const onTimeUpRef = useRef(onTimeUp)
  useEffect(() => {
    onTimeUpRef.current = onTimeUp
  }, [onTimeUp])

  useEffect(() => {
    if (!running) return undefined

    const startedAt = performance.now() - elapsedRef.current
    timeUpFiredRef.current = false

    const tick = (): void => {
      const next = performance.now() - startedAt
      elapsedRef.current = next
      setElapsedMs(next)

      if (mode === 'chrono' && durationMs !== undefined && next >= durationMs) {
        if (!timeUpFiredRef.current) {
          timeUpFiredRef.current = true
          onTimeUpRef.current?.()
        }
        return
      }

      frameRef.current = requestAnimationFrame(tick)
    }

    frameRef.current = requestAnimationFrame(tick)

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
      // Fige le reliquat exact au moment de l'arrêt, plutôt que la dernière
      // frame connue : sans ça, jusqu'à ~16 ms d'écoulé sont perdus à chaque
      // pause/reprise (ex. confirmation d'abandon).
      elapsedRef.current = performance.now() - startedAt
    }
  }, [running, mode, durationMs])

  const displayMs =
    mode === 'chrono' && durationMs !== undefined ? Math.max(0, durationMs - elapsedMs) : elapsedMs

  return (
    <div
      aria-label={t('practice.hud.label')}
      className="flex items-center justify-between gap-3 rounded-2xl bg-surface-low px-3 py-2 md:px-4 md:py-3"
    >
      <div className="flex flex-col">
        <span className="text-[11px] uppercase tracking-wide text-on-surface-variant">
          {t('practice.hud.round_label')}
        </span>
        <span className="font-display text-sm font-semibold tabular-nums text-on-surface">
          {t('practice.hud.round_progress', { current: currentRound, total: totalRounds })}
        </span>
      </div>

      {/* Change jusqu'à une fois par seconde : jamais `aria-live`, un lecteur
          d'écran réannoncerait toute la barre en boucle pendant la partie. */}
      <div aria-hidden="true" className="flex flex-col items-center">
        <span className="text-[11px] uppercase tracking-wide text-on-surface-variant">
          {mode === 'chrono' ? t('practice.hud.time_left') : t('practice.hud.time_elapsed')}
        </span>
        <span className="font-display text-lg font-bold tabular-nums text-primary">
          {formatClock(displayMs)}
        </span>
      </div>

      <div role="status" aria-live="polite" className="flex flex-col items-end">
        <span className="text-[11px] uppercase tracking-wide text-on-surface-variant">
          {t('practice.hud.score_label')}
        </span>
        <span className="font-display text-lg font-bold tabular-nums text-secondary">{score}</span>
      </div>
    </div>
  )
}
